import { datadogRum } from '@datadog/browser-rum'
import type { BaseCheckoutPayFlowModule } from '@payments/lib/checkout-pay/BaseCheckoutPayFlowModule'

interface Deferred {
  resolve: (paymentId: string) => void
  reject: (error: unknown) => void
}

interface CkoSubmitContext {
  paymentTransactionId?: string | number | null
  orderHash?: string | null
}

/**
 * Marker error for cancellation / supersede — consumer UI can `instanceof` 來區分
 * 「使用者主動取消 / race 被覆蓋」vs「真實付款錯誤」，前者不該顯示 error UI。
 */
export class PaymentCancelledError extends Error {
  constructor(reason: string) {
    super(`[CheckoutPayFlowController] cancelled: ${reason}`)
    this.name = 'PaymentCancelledError'
  }
}

/**
 * CheckoutPay (CKO) Flow lifecycle 唯一管理者。
 *
 * `paymentModuleMap` 內 CKO module 是 singleton，所有 consumer 共享同個 SDK component。
 * 把 deferred Promise + callback 註冊邏輯封裝在 controller 內，避免 hook 多 instance
 * 各自持 ref 造成 callback 跟 submit 對不到（前提是 controller 也維持 singleton 使用）。
 */
export class CheckoutPayFlowController {
  private deferred: Deferred | null = null

  /**
   * 註冊 CKO callback 並回傳 cleanup function 給 React useEffect 收尾用。
   *
   * - Register `onPaymentCompleted` / `onError`
   * - Cleanup：若有 in-flight submit → reject + datadog；解掉 callback；unmount component
   */
  public bind(paymentModule: BaseCheckoutPayFlowModule): () => void {
    paymentModule.onPaymentCompleted((_component, payment) => {
      if (!this.deferred) {
        // 不該發生：CKO 回 callback 但 controller 沒 pending submit。可能成因：
        // SDK 重複 fire、cleanup 已先清掉、controller 跟 paymentModule lifecycle 不同步
        datadogRum.addError(
          new Error('[CheckoutPayFlowController] onPaymentCompleted fired with no pending submit'),
          {
            function: 'CheckoutPayFlowController.onPaymentCompleted',
            paymentId: payment.id,
          },
        )
        return
      }
      this.deferred.resolve(payment.id)
      this.deferred = null
    })

    paymentModule.onError((_component, error) => {
      datadogRum.addError(error, { function: 'CheckoutPayFlowController.onError' })
      this.deferred?.reject(error)
      this.deferred = null
    })

    return () => {
      if (this.deferred) {
        // 拆 binding 時還有 in-flight submit — 多半 paymentModule reference 換掉
        datadogRum.addError(
          new Error('[CheckoutPayFlowController] paymentModule unbound while CKO submit in flight'),
          { function: 'CheckoutPayFlowController.bind.cleanup' },
        )
        this.deferred.reject(new Error('[CheckoutPayFlowController] paymentModule changed during CKO submit'))
        this.deferred = null
      }
      paymentModule.onPaymentCompleted(null)
      paymentModule.onError(null)
      paymentModule.unmount()
    }
  }

  /**
   * 觸發 CKO Flow SDK submit，等 `onPaymentCompleted` resolve 拿到 `cko_payment_id`。
   *
   * paymentModule.submit() 為 async（會 await in-flight mount），submit 失敗會走 Promise rejection
   * 而非同步 throw。前一次 submit 尚未結束又呼，舊 deferred 會被覆蓋（並 datadog 警告）。
   */
  public submitAndAwaitPaymentId(
    paymentModule: BaseCheckoutPayFlowModule,
    context: CkoSubmitContext = {},
  ): Promise<string> {
    // 上次 submit 還在 flight 又觸發新的 → 雙擊或 multi-consumer race。
    // 先 reject 舊的 Promise（避免 caller 永久 hang），再建新的。
    this.supersedeDeferred('submitAndAwaitPaymentId', context)

    return new Promise<string>((resolve, reject) => {
      const localDeferred: Deferred = { resolve, reject }
      this.deferred = localDeferred
      paymentModule.submit().catch((submitError: unknown) => {
        datadogRum.addError(
          new Error('[CheckoutPayFlowController] submit() rejected'),
          { function: 'CheckoutPayFlowController.submitAndAwaitPaymentId', ...context, error: submitError },
        )
        if (this.deferred === localDeferred) this.deferred = null
        reject(submitError)
      })
    })
  }

  /**
   * Passive wait — does NOT call `paymentModule.submit()`. For digital wallets
   * (Apple Pay / Google Pay), the sheet must be triggered by user click on the
   * SDK-rendered button; CKO Flow SDK rejects programmatic submit on them.
   */
  public awaitPaymentId(context: CkoSubmitContext = {}): Promise<string> {
    this.supersedeDeferred('awaitPaymentId', context)

    return new Promise<string>((resolve, reject) => {
      this.deferred = { resolve, reject }
    })
  }

  /**
   * Reject 任何還在 in-flight 的 deferred 並清空 ref。
   * 給 submitAndAwaitPaymentId / awaitPaymentId 在覆蓋前呼，避免 caller hang。
   */
  private supersedeDeferred(callerName: string, context: CkoSubmitContext): void {
    if (!this.deferred) return
    datadogRum.addError(
      new Error(`[CheckoutPayFlowController] ${callerName} attempted while another deferred is in flight`),
      { function: `CheckoutPayFlowController.${callerName}`, ...context },
    )
    this.deferred.reject(new PaymentCancelledError('superseded by new submit'))
    this.deferred = null
  }

  public cancelInFlight(reason: string): void {
    if (!this.deferred) return
    this.deferred.reject(new PaymentCancelledError(reason))
    this.deferred = null
  }

  /**
   * Test-only：重置內部狀態。Production code 不該呼此 method。
   */
  public _resetForTest(): void {
    this.deferred = null
  }
}

/**
 * Singleton instance。paymentModule 是 singleton，controller 也必須是，
 * 否則多 hook instance 各別 new 一個 controller 又會回到 ref 不同步的問題。
 */
export const checkoutPayFlowController = new CheckoutPayFlowController()
